import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { s, authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker, SearchableSelect } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems } from './c360';
import { EttnQrButton } from './EttnQr';
import { DocumentPrintDialog } from './DocumentPrintDialog';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  returnSubtypeLabel, waybillBasePath,
  lifecycleLabel, lifecycleTone, approvalLabel, approvalTone,
  toInt, toNullableInt, toDecimal, toNullableGuid, toNullableDate, dateInputValue,
  formatMoney, formatPercent, createClientId, recalcLine,
  lookupLabel, resolvePaymentTypeCode, sanitizeDecimal, fetchProductUnits, convertUnitPrice,
  fetchBoolSetting, SETTING_SHOW_PASSIVE_PRODUCTS,
  fetchDocumentDefaults, findCashPaymentType,
  resolveLineVat, productVatOf, applyVatFallback,
} from './invoiceShared';
import { useDistributorUser } from '../hooks/useDistributorUser';
import { useStockBalances, StockCell } from './stockBalance';
import { usePageActions } from '../contexts/PermissionContext';

// ---- Sayfa-özel sabitler (WaybillsPage.js'ten yerel kopya) ----
const API_BASE = '/api/waybills';

const EMPTY_FORM = {
  textId: '', printedNo: '', printedType: '', receiptNo: '', erpId: '', specialId: '',
  customerId: '', representativeId: '', vendorId: '', warehouseId: '',
  deliveryAdress: '', description1: '', description2: '',
  documentDate: '', dueDate: '', shipDate: '', dueDay: '', year: '',
  type: 0, paymentType: '', paymentTypeId: '', eArchive: '', status: '',
  grossAmount: '', netAmount: '', discountAmount: '',
  discountPercentage1: '', discountPercentage2: '', discountPercentage3: '',
  vatBase: '', vatAmount: '', specialConsumptionTaxAmount: '',
  orderId: '', dispatchId: '',
  // #257 Durum (yaşam döngüsü) + Onay Durumu.
  lifecycleStatus: '', approvalStatus: 0,
};

const EMPTY_DETAIL = {
  id: '',
  clientId: '',
  productId: '',
  warehouseId: '',
  serialNo: '',
  quantity: '1',
  unitOrder: '',
  unit: '',
  unitPrice: '',
  unitNetPrice: '',
  unitVatPrice: '',
  grossAmount: '',
  netAmount: '',
  discountAmount: '',
  discountPercentage1: '',
  discountPercentage2: '',
  discountPercentage3: '',
  discountPercentage4: '',
  discountPercentage5: '',
  discountPercentage6: '',
  discountPercentage7: '',
  discountPercentage8: '',
  vatBase: '',
  vatAmount: '',
  // #760 Kural 1: KDV oranı yeni satırda BOŞ gelir (eskiden sabit '20' idi; ürünün fiyat tanımı yoksa
  // kullanıcı hiç dokunmadan belgeye %20 yazılıyor ve %0/%10'luk üründe tutar sessizce yanlış çıkıyordu).
  vatPercentage: '',
};

// Aynı İrsaliye ekranı, menü konumuna göre Tür'e (Waybill.Type) göre açılır (Satış/Alış/İade).
const WAYBILL_VARIANTS = {
  sales: {
    types: [0],
    title: 'İrsaliyeler', kicker: 'Satış İrsaliyesi',
  },
  purchase: {
    types: [1],
    title: 'Alış İrsaliyeleri', kicker: 'Alış İrsaliyesi',
  },
  // #834 — eski birleşik varyant; menüden erişilmez, eski bağlantılar için korunur.
  return: {
    types: [2, 3, 4],
    title: 'İade İrsaliyeleri', kicker: 'İade İrsaliyesi',
  },
  returnGood: {
    types: [2],
    title: 'Müşteriden Sağlam İade', kicker: 'Müşteriden Sağlam İade İrsaliyesi',
  },
  returnDamaged: {
    types: [4],
    title: 'Müşteriden Bozuk İade', kicker: 'Müşteriden Bozuk İade İrsaliyesi',
  },
  returnVendor: {
    types: [3],
    title: 'Satıcıya İade', kicker: 'Satıcıya İade İrsaliyesi',
  },
  // Konsinye Satış İrsaliyesi (#829) — Type 10. Kalemlerde Seri No okutulur.
  // Alış (8) ve iade (9) BURADAN AYRILDI: #830 ile konsinye alışın kendi ekranı var.
  consignment: {
    types: [10, 9],
    title: 'Konsinye Satış İrsaliyeleri', kicker: 'Konsinye Satış İrsaliyesi',
  },
  // #830 — Konsinye Alış İrsaliyesi (Type 8): tedarikçiden emaneten gelen mal.
  consignmentPurchase: {
    types: [8],
    title: 'Konsinye Alış İrsaliyeleri', kicker: 'Konsinye Alış İrsaliyesi',
  },
};

// variant <-> Type eşlemesi (route'tan/satırdan geri çözümleme için).
function variantForType(type) {
  const t = Number(type);
  if (t === 1) return 'purchase';
  if (t === 2) return 'returnGood';
  if (t === 3) return 'returnVendor';
  if (t === 4) return 'returnDamaged';
  if (t === 8) return 'consignmentPurchase';
  if (t === 10 || t === 9) return 'consignment';
  return 'sales';
}

// ---- Tam-sayfa irsaliye detay stilleri (token tabanlı → dark tema otomatik) ----
const card = 'var(--bg-card, #ffffff)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';
const muted = 'var(--text-secondary, #6b7280)';
const strong = 'var(--card-text-strong, #0b2046)';
const accent = 'var(--accent, #0073ea)';

const st = {
  page: {
    display: 'flex', flexDirection: 'column',
    height: 'calc(100vh - var(--topbar-height, 64px))',
    minHeight: 0, background: 'var(--bg-app, #f3f6f8)',
  },
  topbar: {
    position: 'sticky', top: 0, zIndex: 30,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    padding: '12px 20px', background: card, borderBottom: border, flexWrap: 'wrap',
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  iconCircle: {
    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--accent-soft, rgba(0,115,234,0.12))', color: accent, fontSize: 20,
  },
  kicker: { margin: 0, color: muted, fontSize: 12, fontWeight: 700 },
  title: { margin: 0, color: strong, fontSize: 20, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  codeChip: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, fontWeight: 700,
    padding: '2px 8px', borderRadius: 6, background: 'var(--accent-soft, rgba(0,115,234,0.12))', color: accent,
  },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  scroll: { flex: 1, minHeight: 0, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 },
  band: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, alignItems: 'start' },
  section: { background: card, border, borderRadius: 10, padding: 16 },
  // Bilgi bandı: 3 kolon tek kart içinde, aralarında dikey ayraç.
  bandCard: { background: card, border, borderRadius: 10, padding: '14px 2px' },
  bandGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', columnGap: 0, rowGap: 16, alignItems: 'start' },
  bandCol1: { minWidth: 0, padding: '0 18px' },
  bandCol: { minWidth: 0, padding: '0 18px', borderLeft: border },
  sectionTitle: { margin: '0 0 12px', fontSize: 14, fontWeight: 800, color: strong, display: 'flex', alignItems: 'center', gap: 8 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  grid1: { display: 'grid', gridTemplateColumns: '1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 },
  fieldWide: { gridColumn: 'span 2' },
  label: { color: muted, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.2, display: 'inline-flex', alignItems: 'center', gap: 4 },
  lockTag: { fontSize: 9, fontWeight: 700, color: accent, background: 'var(--accent-soft, rgba(0,115,234,0.12))', borderRadius: 4, padding: '1px 4px' },
  moreBar: {
    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
    padding: '10px 14px', background: card, border, borderRadius: 10,
    color: strong, fontSize: 13, fontWeight: 700,
  },
  itemsCard: { flex: 1, minHeight: 280, background: card, border, borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  itemsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 14px', borderBottom: border, flexWrap: 'wrap' },
  colSearch: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tableWrap: { flex: 1, minHeight: 0, overflow: 'auto' },
  table: { width: '100%', minWidth: 1180, borderCollapse: 'collapse', fontSize: 13 },
  th: {
    position: 'sticky', top: 0, zIndex: 1, textAlign: 'right', padding: '9px 7px',
    background: 'var(--bg-subtle, #f9fafb)', color: muted, fontSize: 11, fontWeight: 800,
    textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: border,
  },
  thLeft: { textAlign: 'left' },
  td: { padding: '7px 7px', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))', textAlign: 'right', color: 'var(--text-primary, #111827)', whiteSpace: 'nowrap' },
  tdLeft: { textAlign: 'left' },
  cellInput: { ...s.input, height: 32, padding: '4px 6px', textAlign: 'right', borderRadius: 6, width: '100%', minWidth: 52 },
  iskInput: { ...s.input, height: 32, padding: '4px 4px', textAlign: 'right', borderRadius: 6, width: 52, minWidth: 44 },
  netCell: { fontWeight: 800, color: accent },
  totbar: {
    position: 'sticky', bottom: 0, zIndex: 30,
    display: 'flex', alignItems: 'stretch', gap: 1,
    background: card, borderTop: border, padding: 0, flexWrap: 'wrap',
  },
  totCell: { flex: 1, minWidth: 120, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 2 },
  totLabel: { color: muted, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  totValue: { color: strong, fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' },
  grandCell: {
    minWidth: 200, padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center',
    background: `linear-gradient(135deg, ${strong}, ${accent})`, color: '#fff',
  },
  chip: { fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: 'var(--accent-soft, rgba(0,115,234,0.12))', color: accent },
};

const dangerBtn = { ...s.btnSecondary, color: 'var(--danger, #e2445c)', borderColor: 'var(--danger, #e2445c)', minWidth: 36, padding: '6px 10px' };

// Bellekte yüklü küçük lookup'lar için aranabilir select fetch'i.
const localFetch = (items, getLabel) => (query) => {
  const q = String(query || '').toLocaleLowerCase('tr');
  return Promise.resolve(
    items
      .map((it) => ({ value: it.id, label: getLabel(it.id) }))
      .filter((o) => !q || String(o.label).toLocaleLowerCase('tr').includes(q))
      .slice(0, 100),
  );
};

export function WaybillDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  // Yeni kayıtta variant query'den (liste new butonu ?variant=... ile yönlendirir).
  const [variant, setVariant] = useState(searchParams.get('variant') || 'sales');
  const cfg = WAYBILL_VARIANTS[variant] || WAYBILL_VARIANTS.sales;
  const defaultType = cfg.types[0];
  // Varyanta karşılık gelen liste sayfası (kapat/sil/iptal sonrası dönüş için).
  const listPath = waybillBasePath(variant);
  // variant → DocumentSeries.DocumentTip (0=Satış, 1=Alış, 2=İade; İrsaliye'de hizmet yok).
  // Konsinye satış tarafı olduğundan Satış (0) serisini kullanır.
  const documentTip = {
    sales: 0, purchase: 1,
    return: 2, returnGood: 2, returnDamaged: 2, returnVendor: 2,
    consignment: 0, consignmentPurchase: 1,
  }[variant] ?? 0;
  // #834 — İade türü artık MENÜDEN gelir; formda seçilmez, yalnız künye olarak gösterilir.
  const isReturnVariant = cfg.types.every((t) => t === 2 || t === 3 || t === 4);
  // Konsinye kalemlerinde Seri No okutulur (WaybillDetailWriteRequest.serialNo).
  // #830 — Konsinye ALIŞ irsaliyesi de konsinyedir: seri okutma onda da açık olmalı. Kontrol
  // varyant ADINA değil belge TÜRÜNE bakar, yoksa yeni bir konsinye varyantı eklendiğinde
  // seri kolonu sessizce kaybolurdu.
  const isConsignment = cfg.types.every((t) => t === 8 || t === 9 || t === 10);

  usePageMeta(isNew ? 'Yeni İrsaliye' : 'İrsaliye Detay', cfg.kicker);

  // Menü aksiyon yetkileri — usePageActions argümansız çağrılır, aktif rota pathname'inden
  // menuId'yi kendi çözer (bu bileşen /waybills, /purchase-waybills, /return-waybills ve
  // /consignment-waybills rotalarına hizmet eder; her rota kendi menüsünün hakkına düşer).
  const { canAdd, canEdit, canDelete } = usePageActions();
  // Yeni kayıt Add, mevcut kayıt Update hakkı ister.
  const canSave = isNew ? canAdd : canEdit;

  const [mode, setMode] = useState(isNew ? 'new' : (searchParams.get('edit') === '1' ? 'edit' : 'view'));
  // Edit'e listeden mi (doğrudan ?edit=1) yoksa sayfada view→Düzenle ile mi girildi?
  const [editFromUrl, setEditFromUrl] = useState(!isNew && searchParams.get('edit') === '1');
  // Düzenleme niyeti (?edit=1) Güncelle hakkını YENEMEZ — hak yoksa mevcut kayıt izlemeye düşer
  // (DetailShell'in permLocked davranışıyla aynı). Yeni kayıt yolu Kaydet gate'iyle kapalıdır.
  const isView = mode === 'view' || (!isNew && !canEdit);
  const [loading, setLoading] = useState(!isNew);
  const [form, setForm] = useState(EMPTY_FORM);
  const [ettn, setEttn] = useState(''); // e-belge ETTN no (salt-okunur; insert'te backend üretir)
  const [waybillDetails, setWaybillDetails] = useState([]);
  // #797.1 Açılıştaki kalemler — rozetteki canlı düşümün referansı (çift sayımı önler).
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);
  const [toast, setToast] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // #255 Kaydet sırasında çift-gönderimi (mükerrer irsaliye) engelle.
  const [saving, setSaving] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [colSearch, setColSearch] = useState({ code: '', name: '' });

  // Lookup'lar
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Talep 833 kural 2 — distribütör kullanıcısında Distribütör alanı seçili + kilitli.
  const distributorUser = useDistributorUser();
  const [customerLabels, setCustomerLabels] = useState({});
  const [productLabels, setProductLabels] = useState({});
  // #249 productId → tanımlı birim adları (ProductUnit); Birim dropdown'ını besler.
  const [productUnits, setProductUnits] = useState({});
  // #252 Kalem rehberinde pasif ürünler gösterilsin mi (genel parametre; varsayılan: gösterme).
  const [showPassiveProducts, setShowPassiveProducts] = useState(false);
  const productConfig = useMemo(
    () => (showPassiveProducts ? RELATION_CONFIGS.productWithGroups : { ...RELATION_CONFIGS.productWithGroups, baseFilter: 'Status ne 0' }),
    [showPassiveProducts],
  );
  useEffect(() => { fetchBoolSetting(SETTING_SHOW_PASSIVE_PRODUCTS, authHeaders(), false).then(setShowPassiveProducts); }, []);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // Hızlı ekleme satırı (klavye-öncelikli) + odak zinciri
  const [draft, setDraft] = useState({ ...EMPTY_DETAIL, clientId: 'draft' });
  const qtyRef = useRef(null);
  const unitPriceRef = useRef(null);
  const isk1Ref = useRef(null);

  // Talep 748 — Seçili depodaki stok, hem ürün ekleme alanında hem eklenmiş kalem
  // satırlarında INFO olarak görünür (kullanıcı stok rakamını görmeden eklememeli).
  const stockProductIds = useMemo(
    () => [...waybillDetails.map((l) => l.productId), draft.productId],
    [waybillDetails, draft.productId],
  );
  const { balanceOf: waybillBalanceOf, ready: stockReady, reload: reloadStock } =
    useStockBalances(form.vendorId, form.warehouseId, stockProductIds);

  // #748 (2. tur) Stok kolonu: KAYITLI bakiye, satırda seçili birime çevrilmiş.
  // #797/#799/#805 Kalem girerken rakam DEĞİŞMEZ — kaydedilince handleSave `reloadStock()` ile
  // defterdeki yeni bakiyeyi okur (gerekçe: stockBalance.js'teki not).
  const renderStockCell = (productId, unitName) => (
    <StockCell
      productId={productId}
      balanceOf={waybillBalanceOf}
      ready={stockReady}
      unitMultiplier={unitsOf(productId).find((u) => u.name === unitName)?.multiplier || 1}
    />
  );

  // ---- Küçük lookup'ları yükle ----
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => r.ok ? r.json() : []),
      fetch('/api/vendors/Select', { headers }).then((r) => r.ok ? r.json() : []),
      fetch('/api/warehouses/Select', { headers }).then((r) => r.ok ? r.json() : []),
      fetch('/api/paymenttypes/Select', { headers }).then((r) => r.ok ? r.json() : []),
    ]).then(([rep, ven, wh, pt]) => {
      setRepresentatives(unwrapLookupItems(rep));
      setVendors(unwrapLookupItems(ven));
      setWarehouses(unwrapLookupItems(wh));
      setPaymentTypes(unwrapLookupItems(pt));
    }).catch(() => {});
  }, []);

  const nameOf = (items, idValue) => {
    if (!idValue) return '-';
    const item = items.find((x) => x.id === idValue);
    return item ? lookupLabel(item) : '-';
  };
  const representativeName = useCallback((v) => nameOf(representatives, v), [representatives]);
  const vendorName = useCallback((v) => nameOf(vendors, v), [vendors]);
  const warehouseName = useCallback((v) => nameOf(warehouses, v), [warehouses]);
  const customerName = useCallback((v) => (v ? (customerLabels[v] || '…') : '-'), [customerLabels]);
  const productName = useCallback((v) => (v ? (productLabels[v] || '…') : '-'), [productLabels]);
  const getPaymentTypeLabel = useCallback((v) => {
    const item = paymentTypes.find((p) => p.id === v);
    return item ? lookupLabel(item) : '-';
  }, [paymentTypes]);
  const paymentTypeIdByCode = useCallback((code) => {
    const n = Number.parseInt(code, 10);
    if (Number.isNaN(n)) return '';
    const item = paymentTypes.find((e) => resolvePaymentTypeCode(e) === n);
    return item?.id || '';
  }, [paymentTypes]);
  // Temsilcinin bölge/ekip bilgisi (lookup item'ında varsa).
  const representativeRegion = useCallback((v) => {
    const item = representatives.find((x) => x.id === v);
    return item ? (item.regionName || item.RegionName || item.description || item.Description || item.groupName || item.GroupName || '') : '';
  }, [representatives]);

  // Aranabilir select fetch'leri — referans sabit kalsın (açık dropdown'da gereksiz yeniden arama olmasın).
  const repFetch = useMemo(() => localFetch(representatives, representativeName), [representatives, representativeName]);
  const vendorFetch = useMemo(() => localFetch(vendors, vendorName), [vendors, vendorName]);
  const warehouseFetch = useMemo(() => localFetch(warehouses, warehouseName), [warehouses, warehouseName]);
  const paymentTypeFetch = useMemo(() => localFetch(paymentTypes, getPaymentTypeLabel), [paymentTypes, getPaymentTypeLabel]);

  const ensureCustomerLabel = useCallback(async (cid) => {
    if (!cid || customerLabels[cid] !== undefined) return;
    try {
      const url = `/api/customers?$top=1&$filter=${encodeURIComponent(`Id eq ${cid}`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) { const rows = getODataRows(await res.json()); if (rows[0]) setCustomerLabels((p) => ({ ...p, [cid]: lookupLabel(rows[0]) })); }
    } catch { /* sessiz */ }
  }, [customerLabels]);

  // Talep 833 kural 3+4 — Müşteri seçilince: Temsilci rut ilişkisinden (RoutePlan), Depo o
  // temsilcinin "Satış Deposu" parametresinden gelir (irsaliye satış hattıdır). Rut eşleşmesi
  // yoksa Temsilci BOŞALIR — kart alanına fallback yok (bkz. DocumentDefaultsController).
  const applyCustomerDefaults = useCallback(async (customerId) => {
    if (!customerId) return;
    const d = await fetchDocumentDefaults(customerId, authHeaders());
    if (!d) return;
    if (d.representativeId && d.representativeName) {
      setRepresentatives((prev) => (prev.some((x) => x.id === d.representativeId)
        ? prev
        : [...prev, { id: d.representativeId, textId: d.representativeTextId, name: d.representativeName }]));
    }
    setForm((c) => {
      const next = { ...c, representativeId: d.representativeId || '' };
      if (d.salesWarehouseId) next.warehouseId = d.salesWarehouseId;
      if (d.vendorId && !distributorUser.isDistributor) next.vendorId = d.vendorId;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorUser.isDistributor]);

  const ensureProductLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((x) => x && productLabels[x] === undefined))];
    if (!need.length) return;
    try {
      const filter = need.map((x) => `Id eq ${x}`).join(' or ');
      const url = `/api/products?$top=500&light=1&$filter=${encodeURIComponent(`(${filter})`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) {
        const found = {}; const bases = {};
        getODataRows(await res.json()).forEach((p) => { found[p.id] = lookupLabel(p); bases[p.id] = p.unit1; });
        setProductLabels((prev) => { const next = { ...prev }; need.forEach((x) => { next[x] = found[x] || '-'; }); return next; });
        // #249 Birimleri ürün tanımından (ProductUnit) çek — skalar Unit1..5 DEĞİL.
        const headers = authHeaders();
        need.forEach((pid) => {
          fetchProductUnits(pid, headers, bases[pid]).then((units) => setProductUnits((prev) => (prev[pid] ? prev : { ...prev, [pid]: units })));
        });
      }
    } catch { /* sessiz */ }
  }, [productLabels]);

  const rowToForm = useCallback((row) => ({
    textId: row.textId || '', printedNo: row.printedNo || '', printedType: row.printedType ?? '',
    receiptNo: row.receiptNo || '', erpId: row.erpId || '', specialId: row.specialId || '',
    customerId: row.customerId || '', representativeId: row.representativeId || '', vendorId: row.vendorId || '', warehouseId: row.warehouseId || '',
    deliveryAdress: row.deliveryAdress || '', description1: row.description1 || '', description2: row.description2 || '',
    documentDate: dateInputValue(row.documentDate), dueDate: dateInputValue(row.dueDate), shipDate: dateInputValue(row.shipDate),
    dueDay: row.dueDay ?? '', year: row.year ?? '', type: row.type ?? 0,
    paymentType: row.paymentType ?? '', paymentTypeId: paymentTypeIdByCode(row.paymentType),
    eArchive: row.eArchive ?? '', status: row.status ?? '',
    // #257 Durum (yaşam döngüsü) + Onay Durumu.
    lifecycleStatus: row.lifecycleStatus ?? '', approvalStatus: row.approvalStatus ?? 0,
    grossAmount: row.grossAmount ?? '', netAmount: row.netAmount ?? '', discountAmount: row.discountAmount ?? '',
    discountPercentage1: row.discountPercentage1 ?? '', discountPercentage2: row.discountPercentage2 ?? '', discountPercentage3: row.discountPercentage3 ?? '',
    vatBase: row.vatBase ?? '', vatAmount: row.vatAmount ?? '', specialConsumptionTaxAmount: row.specialConsumptionTaxAmount ?? '',
    orderId: row.orderId || '', dispatchId: row.dispatchId || '',
  }), [paymentTypeIdByCode]);

  // Yükleme: kademeli iskonto kolonları boş ama iskonto₺ varsa → İsk-1%'e tek-kademe eşdeğeri yaz.
  const rowToDetail = (row) => {
    const quantity = toDecimal(row.quantity);
    const unitPrice = toDecimal(row.unitPrice);
    const gross = quantity * unitPrice;
    const discountAmount = toDecimal(row.discountAmount);
    const noPct = [1, 2, 3, 4].every((n) => toDecimal(row[`discountPercentage${n}`]) === 0);
    let pct1 = row.discountPercentage1 ?? '';
    if (noPct && gross > 0 && discountAmount > 0) {
      pct1 = String(Number(((discountAmount / gross) * 100).toFixed(2)));
    }
    return recalcLine({
      id: row.id || '', clientId: row.id || createClientId(),
      productId: row.productId || '', warehouseId: row.warehouseId || '',
      serialNo: row.serialNo ?? '',
      quantity: row.quantity ?? '', unitOrder: row.unitOrder ?? '', unit: row.unit ?? '',
      unitPrice: row.unitPrice ?? '', unitNetPrice: row.unitNetPrice ?? '', unitVatPrice: row.unitVatPrice ?? '',
      grossAmount: row.grossAmount ?? '', netAmount: row.netAmount ?? '', discountAmount: row.discountAmount ?? '',
      discountPercentage1: pct1,
      discountPercentage2: row.discountPercentage2 ?? '', discountPercentage3: row.discountPercentage3 ?? '',
      discountPercentage4: row.discountPercentage4 ?? '', discountPercentage5: row.discountPercentage5 ?? '',
      discountPercentage6: row.discountPercentage6 ?? '', discountPercentage7: row.discountPercentage7 ?? '',
      discountPercentage8: row.discountPercentage8 ?? '',
      vatBase: row.vatBase ?? '', vatAmount: row.vatAmount ?? '', vatPercentage: row.vatPercentage ?? '',
    });
  };

  // ---- İrsaliyeyi yükle ----
  const load = useCallback(async () => {
    if (isNew) {
      const now = new Date();
      setForm({ ...EMPTY_FORM, type: defaultType, documentDate: dateInputValue(now), year: now.getFullYear().toString() });
      setWaybillDetails([]); setDeletedDetailIds([]); setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const headers = authHeaders();
      const wbUrl = `/api/waybills?$top=1&$filter=${encodeURIComponent(`Id eq ${id}`)}`;
      const wbRes = await fetch(wbUrl, { headers });
      if (!wbRes.ok) throw new Error('İrsaliye alınamadı');
      const wbRow = getODataRows(await wbRes.json())[0];
      if (!wbRow) throw new Error('İrsaliye bulunamadı');
      setForm(rowToForm(wbRow));
      setEttn(wbRow.ettn || wbRow.Ettn || '');
      setVariant(variantForType(wbRow.type));
      if (wbRow.customerId) ensureCustomerLabel(wbRow.customerId);

      // #795 Kalem sırası LineOrder'dadır (#757/#758) — UnitOrder birim slotudur, sıralama ekseni değil.
      // İkincil Id: LineOrder'ı 0 kalan eski kayıtlarda giriş sırası korunur.
      const detUrl = `/api/waybilldetails?$filter=${encodeURIComponent(`WaybillId eq ${id}`)}&$orderby=${encodeURIComponent('LineOrder asc,Id asc')}`;
      const detRes = await fetch(detUrl, { headers });
      const details = detRes.ok ? getODataRows(await detRes.json()).map(rowToDetail) : [];
      setWaybillDetails(details);
      ensureProductLabels(details.map((d) => d.productId).filter(Boolean));
    } catch (e) {
      showToast('error', e.message || 'İrsaliye alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Talep 833 kural 2 — Distribütör kullanıcısında Distribütör alanı kendi distribütörüne
  // sabitlenir (yalnız YENİ belgede; mevcut belgenin distribütörü açılışta değiştirilmez).
  useEffect(() => {
    if (!isNew || !distributorUser.isDistributor || !distributorUser.vendorId) return;
    setForm((c) => (c.vendorId === distributorUser.vendorId ? c : { ...c, vendorId: distributorUser.vendorId }));
  }, [isNew, distributorUser.isDistributor, distributorUser.vendorId]);

  // Talep 833 kural 5 — Yeni irsaliyede Ödeme Tipi varsayılanı NAKİT (Fatura ile aynı alan çifti:
  // paymentTypeId Guid + paymentType kod). Lookup seed edilmemişse boş kalır.
  useEffect(() => {
    if (!isNew || !paymentTypes.length || form.paymentTypeId) return;
    const cash = findCashPaymentType(paymentTypes);
    if (cash) setForm((c) => (c.paymentTypeId ? c : { ...c, paymentTypeId: cash.id, paymentType: 1 }));
  }, [isNew, paymentTypes, form.paymentTypeId]);

  // Ödeme tipi kodu → id eşlemesini lookup yüklenince tamamla.
  useEffect(() => {
    if (!paymentTypes.length || form.paymentTypeId || form.paymentType === '') return;
    const mapped = paymentTypeIdByCode(form.paymentType);
    if (mapped) setForm((c) => (c.paymentTypeId ? c : { ...c, paymentTypeId: mapped }));
  }, [paymentTypes, form.paymentTypeId, form.paymentType, paymentTypeIdByCode]);

  const setValue = (key, value) => setForm((c) => ({ ...c, [key]: value }));

  // #759 Başlık deposu değişince mevcut TÜM kalemlerin deposunu da yeni depoya eşitle.
  // İrsaliye tek-depo mantığındadır; kalemler başlık deposunu takip etmeli. Aksi halde depo
  // değiştirilip yeni kalem eklenince önceki kalemler eski depoda kalır → belge kendi içinde
  // tutarsız olur ve stok yanlış depodan düşer. (Fatura ekranındaki changeWarehouse ile aynı desen.)
  const changeWarehouse = (val) => {
    setValue('warehouseId', val);
    setWaybillDetails((cur) => cur.map((row) => (
      String(row.warehouseId ?? '') === String(val ?? '') ? row : { ...row, warehouseId: val || '' }
    )));
  };

  // #246 Vade Günü ↔ Vade Tarihi çift-yönlü senkron (taban = İrsaliye Tarihi, yoksa bugün).
  const daysBetween = (from, to) => {
    const a = new Date(from); a.setHours(0, 0, 0, 0);
    const b = new Date(to); b.setHours(0, 0, 0, 0);
    return Math.round((b.getTime() - a.getTime()) / 86400000);
  };
  const docBaseDate = (docStr) => {
    const d = docStr ? new Date(docStr) : new Date();
    return Number.isNaN(d.getTime()) ? new Date() : d;
  };
  const onDueDayChange = (val) => {
    const days = Number.parseInt(val, 10);
    if (val === '' || Number.isNaN(days)) { setValue('dueDay', val); return; }
    const due = docBaseDate(form.documentDate); due.setDate(due.getDate() + days);
    setForm((c) => ({ ...c, dueDay: String(days), dueDate: dateInputValue(due) }));
  };
  const onDueDateChange = (val) => {
    if (!val) { setForm((c) => ({ ...c, dueDate: '' })); return; }
    const diff = daysBetween(docBaseDate(form.documentDate), val);
    setForm((c) => ({ ...c, dueDate: val, dueDay: Number.isFinite(diff) ? String(diff) : c.dueDay }));
  };
  const onDocDateChange = (val) => {
    setForm((c) => {
      const next = { ...c, documentDate: val };
      const days = Number.parseInt(c.dueDay, 10);
      if (val && !Number.isNaN(days)) {
        const due = new Date(val);
        if (!Number.isNaN(due.getTime())) { due.setDate(due.getDate() + days); next.dueDate = dateInputValue(due); }
      }
      return next;
    });
  };

  // ---- Toplamlar ----
  // #762 Toplamlar SATIR LİSTESİNDEN parametrik (bkz. handleSave taslak satır işleme).
  const computeTotals = (rows) => {
    const dt = rows.reduce((acc, row) => ({
      grossAmount: acc.grossAmount + toDecimal(row.grossAmount),
      netAmount: acc.netAmount + toDecimal(row.netAmount),
      discountAmount: acc.discountAmount + toDecimal(row.discountAmount),
      vatBase: acc.vatBase + toDecimal(row.vatBase),
      vatAmount: acc.vatAmount + toDecimal(row.vatAmount),
    }), { grossAmount: 0, netAmount: 0, discountAmount: 0, vatBase: 0, vatAmount: 0 });
    const any = rows.length > 0;
    return {
      grossAmount: any ? dt.grossAmount : toDecimal(form.grossAmount),
      netAmount: any ? dt.netAmount : toDecimal(form.netAmount),
      discountAmount: any ? dt.discountAmount : toDecimal(form.discountAmount),
      vatBase: any ? dt.vatBase : toDecimal(form.vatBase),
      vatAmount: any ? dt.vatAmount : toDecimal(form.vatAmount),
      sct: toDecimal(form.specialConsumptionTaxAmount),
    };
  };
  const totals = computeTotals(waybillDetails);

  // ---- Kalem işlemleri (tüm hesaplı değişiklikler kademeli recalcLine'dan geçer) ----
  const updateDetailRow = (clientId, key, value) => {
    setWaybillDetails((cur) => cur.map((row) => {
      if (row.clientId !== clientId) return row;
      const next = { ...row, [key]: value };
      const calcKeys = ['quantity', 'unitPrice', 'vatPercentage', 'discountPercentage1', 'discountPercentage2', 'discountPercentage3', 'discountPercentage4'];
      return calcKeys.includes(key) ? recalcLine(next) : next;
    }));
  };

  const removeDetailRow = (row) => {
    if (row.id) setDeletedDetailIds((cur) => (cur.includes(row.id) ? cur : [...cur, row.id]));
    setWaybillDetails((cur) => cur.filter((x) => x.clientId !== row.clientId));
  };

  // #249 Birim hücresi: ürünün tanımlı birimlerinden (ProductUnit) açılır liste. Baz birim = ilk.
  const unitsOf = (pid) => productUnits[pid] || [];
  const renderUnitCell = (pid, unitVal, onChange) => {
    const opts = unitsOf(pid);
    const names = opts.map((u) => u.name);
    const cur = unitVal || names[0] || '';
    if (isView) return cur || '-';
    return (
      <select style={{ ...st.cellInput, textAlign: 'left', minWidth: 64 }} value={cur} onChange={(e) => onChange(e.target.value)}>
        {names.length === 0 && <option value="">-</option>}
        {names.map((u) => <option key={u} value={u}>{u}</option>)}
        {cur && !names.includes(cur) && <option value={cur}>{cur}</option>}
      </select>
    );
  };

  // #253 Birim değişince fiyatı yeni çarpana göre ölçekle + satırı yeniden hesapla.
  const changeRowUnit = (row, newUnit) => {
    const units = unitsOf(row.productId);
    const newPrice = convertUnitPrice(row.unitPrice, units, row.unit || units[0]?.name, newUnit);
    setWaybillDetails((cur) => cur.map((r) => (r.clientId === row.clientId ? recalcLine({ ...r, unit: newUnit, unitPrice: newPrice }) : r)));
  };
  const changeDraftUnit = (newUnit) => {
    const units = unitsOf(draft.productId);
    const newPrice = convertUnitPrice(draft.unitPrice, units, draft.unit || units[0]?.name, newUnit);
    setDraft((d) => recalcLine({ ...d, unit: newUnit, unitPrice: newPrice }));
  };

  // Ürün seçilince /api/prices/Effective'den fiyat+kdv doldur (taslak satıra).
  // #760 KDV oranı fiyat tanımından; fiyatta tanımlı değilse ÜRÜN KARTINDAN (resolveLineVat).
  // fallbackVat çağıran tarafından geçirilir (ALIŞ belgesinde ürünün alış oranı).
  const fetchEffectivePriceInto = async (productId, applyFn, fallbackVat) => {
    if (!productId) return;
    try {
      const params = new URLSearchParams({ productId });
      if (form.customerId) params.append('customerId', form.customerId);
      if (form.vendorId) params.append('vendorId', form.vendorId);
      const res = await fetch(`/api/prices/Effective?${params}`, { headers: authHeaders() });
      if (!res.ok) {
        // #760 (2. tur) Uç hata verdiyse (403/5xx) kalem KDV'siz kalmasın — oran ürün kartından.
        applyVatFallback(applyFn, fallbackVat);
        return;
      }
      const price = await res.json();
      if (!price) {
        // #760 Kural 4 — fiyat tanımı YOK: KDV ürün kartından gelir, %20'de takılı KALMAZ.
        showToast('error', 'Bu ürün için tanımlı fiyat yok — manuel giriniz');
        applyVatFallback(applyFn, fallbackVat);
        return;
      }
      const patch = { unitPrice: String(price.salePrice ?? 0) };
      const vat = resolveLineVat(price, fallbackVat);
      if (vat != null) patch.vatPercentage = vat;
      applyFn(patch);
    } catch {
      // #760 (2. tur) Ağ hatasında da KDV yazılsın; fiyat elle girilir.
      applyVatFallback(applyFn, fallbackVat);
    }
  };

  const setDraftProduct = (val, item) => {
    if (val && item) setProductLabels((p) => ({ ...p, [val]: lookupLabel(item) }));
    // #249 ürünün tanımlı birimlerini (ProductUnit) çek; taslağın birimini baz birime ayarla.
    setDraft((d) => recalcLine({ ...d, productId: val, unit: '' }));
    if (val) {
      // #755 VARSAYILAN BİRİM: ürün kartındaki "Varsayılan Ürün Birimi" (Product.Unit = 1..5) irsaliye
      // kaleminde de ÖN-SEÇİLİR (Sipariş'teki desen). 1/boş = taban; lookup'ta yoksa taban birim.
      const duo = Number(item?.unit);
      const defaultUnitName = duo >= 1 && duo <= 5 ? String(item?.[`unit${duo}`] ?? '').trim() : '';
      // #760 (2. tur) Birim çekimi reddedilirse fiyat/KDV zinciri de düşüyordu — taban birimle devam.
      fetchProductUnits(val, authHeaders(), item?.unit1).catch(() => []).then((units) => {
        setProductUnits((p) => ({ ...p, [val]: units }));
        const base = units[0]?.name || '';
        const wanted = defaultUnitName && units.some((u) => u.name === defaultUnitName) ? defaultUnitName : base;
        setDraft((d) => (d.productId === val && !d.unit ? { ...d, unit: wanted } : d));
        // #760 ALIŞ irsaliyesinde ürünün ALIŞ KDV oranı fallback'tir.
        // #755 Efektif fiyat TABAN birim fiyatıdır; ön-seçili birim tabandan farklıysa çarpanla ölçeklenir.
        fetchEffectivePriceInto(
          val,
          (patch) => setDraft((d) => {
            if (d.productId !== val) return d;
            const scaledPrice = wanted && wanted !== base
              ? convertUnitPrice(patch.unitPrice, units, base, wanted)
              : patch.unitPrice;
            return recalcLine({ ...d, ...patch, ...(patch.unitPrice != null ? { unitPrice: scaledPrice } : {}) });
          }),
          productVatOf(item, variant === 'purchase'),
        );
      });
      ensureProductLabels([val]);
      setTimeout(() => qtyRef.current?.focus(), 60);
    }
  };

  // #766 KALEM EKLEMEDEN ÖNCE zorunlu başlık alanları dolu olmalı (Depo belgelerindeki desenin ticari
  // belgelere taşınması). Kalem müşteri (fiyat), depo (kalemin deposu başlıktan türetilir) ve temsilci
  // bağlamına yazılır. Ödeme tipi kalemle ilgisiz — kendi kayıt-anı kontrolünde kalır.
  const headerReady = !!(form.customerId && form.representativeId && form.warehouseId);
  const headerHint = 'Önce müşteri, depo ve temsilci seçin';

  /**
   * #762 Taslak satırı DOĞRULAYIP satıra çevirir; ihlalde gerekçeyi gösterip null döner. commitDraft
   * ("+ Ekle"/Enter) ile handleSave ("Kaydet") AYNI bu fonksiyonu kullanır — kalemi yazıp doğrudan
   * Kaydet'e basıldığında satır kaybolmasın ("ilk Kaydet kayıt yapmıyor" şikâyeti).
   */
  const buildDraftRow = (existingCount) => {
    // #766 Zorunlu başlık alanları dolmadan kalem yazılamaz — hata kayıt anına ERTELENMEZ.
    if (!headerReady) { showToast('error', headerHint); return null; }
    if (!draft.productId) { showToast('error', 'Önce ürün seçin'); return null; }
    if (toDecimal(draft.quantity) <= 0) { showToast('error', 'Miktar 0’dan büyük olmalı'); return null; }
    return recalcLine({
      ...draft,
      clientId: createClientId(),
      warehouseId: form.warehouseId || '',
      // #757/#758 Kalem SIRASI lineOrder'a yazılır. Eskiden unitOrder'a yazılıyordu; unitOrder ise
      // BİRİM SLOTU'dur (2..5 stok çevriminde Unit{N}Multiplier ile çarpılır) → 2. kalem, taban birim
      // seçilse bile çarpanla stoğa giriyordu. Slotu artık sunucu birim ADINDAN çözer.
      lineOrder: (existingCount ?? waybillDetails.length) + 1,
    });
  };

  /** Taslak satırda ürün var mı? (Kaydet'in taslağı kapsaması için.) */
  const draftHasContent = () => Boolean(draft.productId);

  const commitDraft = () => {
    const row = buildDraftRow();
    if (!row) return;
    setWaybillDetails((cur) => [...cur, row]);
    setDraft({ ...EMPTY_DETAIL, clientId: 'draft' });
    showToast('success', `Satır eklendi · ${productName(row.productId)}`);
  };

  // ---- Kaydet ----
  const buildPayload = (rows = waybillDetails) => {
    const t = computeTotals(rows);
    const selectedPt = paymentTypes.find((x) => x.id === form.paymentTypeId);
    const code = resolvePaymentTypeCode(selectedPt);
    return {
      id: isNew ? '00000000-0000-0000-0000-000000000000' : id,
      textId: form.textId, printedNo: form.printedNo, printedType: toInt(form.printedType),
      receiptNo: form.receiptNo || null, erpId: form.erpId || null, specialId: form.specialId || null,
      customerId: form.customerId, representativeId: form.representativeId, vendorId: toNullableGuid(form.vendorId), warehouseId: form.warehouseId,
      deliveryAdress: form.deliveryAdress || null, description1: form.description1 || null, description2: form.description2 || null,
      documentDate: toNullableDate(form.documentDate), dueDate: toNullableDate(form.dueDate), shipDate: toNullableDate(form.shipDate),
      dueDay: toNullableInt(form.dueDay), year: toInt(form.year), type: toInt(form.type),
      paymentType: code ?? toInt(form.paymentType), paymentTypeId: toNullableGuid(form.paymentTypeId),
      eArchive: toInt(form.eArchive), status: toInt(form.status),
      // #257 Durum (yaşam döngüsü) + Onay Durumu — eski status'ten bağımsız kolonlar.
      lifecycleStatus: toInt(form.lifecycleStatus), approvalStatus: toInt(form.approvalStatus),
      grossAmount: t.grossAmount, netAmount: t.netAmount, discountAmount: t.discountAmount,
      discountPercentage1: toDecimal(form.discountPercentage1), discountPercentage2: toDecimal(form.discountPercentage2), discountPercentage3: toDecimal(form.discountPercentage3),
      vatBase: t.vatBase, vatAmount: t.vatAmount, specialConsumptionTaxAmount: toDecimal(form.specialConsumptionTaxAmount),
      orderId: toNullableGuid(form.orderId), dispatchId: toNullableGuid(form.dispatchId),
    };
  };

  const buildDetailPayload = (row, index) => ({
    id: row.id || '00000000-0000-0000-0000-000000000000',
    discountAmount: toDecimal(row.discountAmount),
    discountPercentage1: toDecimal(row.discountPercentage1), discountPercentage2: toDecimal(row.discountPercentage2),
    discountPercentage3: toDecimal(row.discountPercentage3), discountPercentage4: toDecimal(row.discountPercentage4),
    discountPercentage5: toDecimal(row.discountPercentage5), discountPercentage6: toDecimal(row.discountPercentage6),
    discountPercentage7: toDecimal(row.discountPercentage7), discountPercentage8: toDecimal(row.discountPercentage8),
    grossAmount: toDecimal(row.grossAmount),
    waybillId: isNew ? '00000000-0000-0000-0000-000000000000' : id,
    netAmount: toDecimal(row.netAmount), productId: row.productId, quantity: toDecimal(row.quantity),
    unitNetPrice: toDecimal(row.unitNetPrice),
    // #757 unitOrder SUNUCUDA birim adından çözülür; buradaki değer yok sayılır (eski istemcilerle
    // uyum için taban gönderilir). #758 Kalem sırası lineOrder'da.
    unitOrder: 1,
    lineOrder: index + 1,
    unit: row.unit || productUnits[row.productId]?.[0]?.name || null,
    unitPrice: toDecimal(row.unitPrice), unitVatPrice: toDecimal(row.unitVatPrice),
    vatAmount: toDecimal(row.vatAmount), vatBase: toDecimal(row.vatBase), vatPercentage: toDecimal(row.vatPercentage),
    warehouseId: row.warehouseId || form.warehouseId,
    // Konsinye kalemlerinde okutulan seri no (diğer varyantlarda boş → null).
    serialNo: (row.serialNo || '').trim() || null,
  });

  const handleSave = async ({ approve = false, lifecycle = null } = {}) => {
    if (saving) return; // #255 çift-tık koruması
    // #761 İrsaliye Kodu zorunlu değil — boşsa backend DocumentSeries'ten otomatik atar (Fatura gibi).
    // #245 Distribütör opsiyonel — zorunlu değil.
    if (!form.customerId || !form.representativeId || !form.warehouseId) { showToast('error', 'Müşteri, temsilci ve depo seçimi zorunludur'); return; }
    if (!form.paymentTypeId) { showToast('error', 'Ödeme tipi seçimi zorunludur'); return; }
    // #762 KAYDET TASLAK SATIRI DA KAPSAR (bkz. buildDraftRow): kalemi yazıp "+ Ekle"ye basmadan
    // Kaydet'e basıldığında satır sayılmıyor, ilk Kaydet düşüyor ve ancak İKİNCİ Kaydet kaydediyordu.
    let rows = waybillDetails;
    if (draftHasContent()) {
      const row = buildDraftRow(waybillDetails.length);
      if (!row) return;
      rows = [...waybillDetails, row];
      setWaybillDetails(rows);
      setDraft({ ...EMPTY_DETAIL, clientId: 'draft' });
    }
    if (rows.length === 0) { showToast('error', 'En az bir irsaliye kalemi girilmelidir'); return; }
    const invalid = rows.find((r) => !r.productId || !(r.warehouseId || form.warehouseId) || toDecimal(r.quantity) <= 0);
    if (invalid) { showToast('error', 'Kalemlerde ürün, depo ve miktar zorunludur'); return; }
    const payload = buildPayload(rows);
    // #257 "Taslak Kaydet"/"Kaydet" Durum'u belirler; "Onayla" Onay Durumu'nu Onaylı yapar.
    if (lifecycle !== null) payload.lifecycleStatus = lifecycle;
    if (approve) payload.approvalStatus = 1;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ waybill: payload, details: rows.map(buildDetailPayload), deletedDetailIds }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || data?.Message || data?.error || data?.Error || 'İrsaliye kaydedilemedi');
      showToast('success', approve ? 'İrsaliye onaylandı'
        : (lifecycle === 0 ? 'Taslak kaydedildi' : (isNew ? 'İrsaliye oluşturuldu' : 'İrsaliye güncellendi')));
      // #797/#799/#805 Belge deftere işlendi → stok hücreleri artık YENİ bakiyeyi göstermeli.
      reloadStock();
      const newId = data?.data?.id || data?.Data?.id || data?.id;
      // #762 YENİ BELGE KAYDEDİLDİKTEN SONRA GÖRÜNTÜLEME MODUNA GEÇ.
      //
      // Eskiden bu dal yalnız `navigate(...)` yapıp dönüyordu: rota parametresi değiştiği için
      // bileşen yeniden MOUNT OLMAZ, dolayısıyla `mode` state'i 'new' olarak kalıyordu. Kullanıcının
      // gördüğü tam olarak buydu — başlık "Yeni Kayıt"tan "Düzenleme"ye dönüyor, Kaydet butonu aktif
      // duruyor ve ikinci basış aynı belgeyi bir daha kaydetmeye çalışıp hata veriyordu.
      // Mod, navigasyondan ÖNCE çekilir; `load()` yeni id'nin effect'iyle zaten koşar.
      setMode('view');
      if (isNew && newId) { navigate(`${listPath}/${newId}`, { replace: true }); return; }
      load();
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'İrsaliye silindi');
      navigate(listPath);
    } catch (e) { showToast('error', e.message || 'Silme sırasında hata'); setDeleteConfirm(false); }
  };

  // ---- Filtrelenmiş kalem listesi (kolon-içi arama) ----
  const filteredDetails = useMemo(() => waybillDetails.filter((row) => {
    const label = String(productName(row.productId));
    const code = label.split(' - ')[0];
    if (colSearch.name && !label.toLowerCase().includes(colSearch.name.toLowerCase())) return false;
    if (colSearch.code && !code.toLowerCase().includes(colSearch.code.toLowerCase())) return false;
    return true;
  }), [waybillDetails, colSearch, productName]);
  const totalQty = waybillDetails.reduce((sum, r) => sum + toDecimal(r.quantity), 0);

  // ---- Alan render yardımcıları ----
  // NOT: Bunlar JSX bileşeni (<Field/>) olarak DEĞİL, fonksiyon olarak ({Field({...})})
  // çağrılır — gövde içinde tanımlı bir bileşeni JSX ile kullanmak her render'da
  // remount'a ve input'ta odak kaybına yol açar.
  const Field = ({ label, k, type = 'text', wide = false, mono = false, multiline = false, readOnly = false, locked = false, placeholder = '' }) => (
    <div key={k} style={{ ...st.field, ...(wide ? st.fieldWide : null) }}>
      <label style={st.label}>{label}{locked && <span style={st.lockTag} title="Sistem tarafından üretilir, değiştirilemez">🔒 oto</span>}</label>
      {multiline ? (
        <textarea style={{ ...s.input, minHeight: 64, resize: 'vertical', borderRadius: 6 }} value={form[k] ?? ''} readOnly={isView || readOnly} placeholder={placeholder} onChange={(e) => setValue(k, e.target.value)} />
      ) : (
        <input type={type} style={{ ...(isView || readOnly ? s.inputView : s.input), borderRadius: 6, ...(mono ? { fontFamily: 'ui-monospace, monospace' } : null) }} value={form[k] ?? ''} readOnly={isView || readOnly} placeholder={placeholder} onChange={(e) => setValue(k, e.target.value)} />
      )}
    </div>
  );

  // locked: düzenleme modunda bile salt-okunur (Talep 833 kural 2 — distribütör kullanıcısı).
  // #759 `onPick`: seçim yalnız alanı yazmakla kalmayıp yan etki gerektiriyorsa (depo değişince
  // kalemlerin deposunu eşitlemek) devreye girer. Fatura/Sipariş ekranlarındaki imzayla aynı.
  const SelectField = ({ label, k, getLabel, fetchOptions, wide = false, locked = false, onPick }) => (
    <div key={k} style={{ ...st.field, ...(wide ? st.fieldWide : null) }}>
      <label style={st.label}>{label}{locked && <span style={st.lockTag} title="Distribütör kullanıcısı — kendi distribütörü dışında belge kesemez">🔒 oto</span>}</label>
      {isView || locked ? (
        <input style={{ ...s.inputView, borderRadius: 6 }} readOnly value={getLabel(form[k]) || '-'} />
      ) : (
        <SearchableSelect
          value={form[k] || ''}
          selectedLabel={form[k] ? getLabel(form[k]) : ''}
          onChange={(val) => (onPick ? onPick(val) : setValue(k, val))}
          fetchOptions={fetchOptions}
          minChars={0}
          placeholder="Ara / seç…"
        />
      )}
    </div>
  );

  if (loading) {
    return <div style={s.container}><div style={{ padding: 40, color: muted }}>Yükleniyor…</div></div>;
  }

  return (
    <div style={s.container}>
      <div style={st.page}>
        {/* ① ÜST BAR */}
        <div style={st.topbar}>
          <div style={st.topLeft}>
            <div style={st.iconCircle}>📋</div>
            <div style={{ minWidth: 0 }}>
              <p style={st.kicker}>İrsaliye Yönetimi · {cfg.kicker} · {isNew ? 'Yeni Kayıt' : isView ? 'Görüntüleme' : 'Düzenleme'}</p>
              <h2 style={st.title}>
                {isNew ? 'Yeni İrsaliye' : 'İrsaliye Detay'}
                {!isNew && form.textId && <span style={st.codeChip}>{form.textId}</span>}
                {/* #257 Durum (yaşam döngüsü) + Onay Durumu ayrı rozetler. */}
                {!isNew && form.lifecycleStatus !== '' && <StatusBadge tone={lifecycleTone(form.lifecycleStatus)}>{lifecycleLabel(form.lifecycleStatus)}</StatusBadge>}
                {!isNew && <StatusBadge tone={approvalTone(form.approvalStatus)}>{approvalLabel(form.approvalStatus)}</StatusBadge>}
              </h2>
            </div>
          </div>
          <div style={st.actions}>
            {isView ? (
              <>
                {canAdd && <button style={s.btnSecondary} onClick={() => navigate(`${listPath}/new?variant=${variant}`)}>Yeni</button>}
                {!isNew && <button style={s.btnSecondary} onClick={() => setPrintOpen(true)}>Ön İzleme</button>}
                {!isNew && <button style={s.btnSecondary} onClick={() => setPrintOpen(true)}>Yazdır</button>}
                {canEdit && <button style={s.btnSecondary} onClick={() => { setEditFromUrl(false); setMode('edit'); }}>Düzenle</button>}
                {canDelete && <button style={dangerBtn} onClick={() => setDeleteConfirm(true)}>Sil</button>}
                <button style={s.btnSecondary} onClick={() => navigate(listPath)}>Kapat</button>
                {/* #257 Onayla artık Durum'u değil Onay Durumu'nu Onaylı yapar.
                    Mevcut kaydı yazdığı için Update (canEdit) hakkına kapılıdır. */}
                {canEdit && toInt(form.approvalStatus) !== 1 && <button style={s.btnPrimary} onClick={() => handleSave({ approve: true })} disabled={saving}>Onayla</button>}
              </>
            ) : (
              <>
                <button style={s.btnSecondary} onClick={() => { if (isNew || editFromUrl) navigate(listPath); else { setMode('view'); load(); } }}>İptal</button>
                {/* #257 İki kaydet düğmesi: Taslak Kaydet (Durum=Taslak) ve Kaydet (Durum=Aktif). */}
                {canSave && <button style={s.btnSecondary} onClick={() => handleSave({ lifecycle: 0 })} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Taslak Kaydet'}</button>}
                {canSave && <button style={s.btnPrimary} onClick={() => handleSave({ lifecycle: 1 })} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>}
              </>
            )}
          </div>
        </div>

        <div style={st.scroll}>
          {/* ③ BİLGİ BANDI — 3 kolon tek kart */}
          <section style={st.bandCard}>
            <div style={st.bandGrid}>
            <div style={st.bandCol1}>
              <h3 style={st.sectionTitle}>📄 İrsaliye Bilgileri</h3>
              <div style={st.grid2}>
                {Field({ label: 'İrsaliye Kodu', k: 'textId', mono: true, wide: true, readOnly: true, locked: true, placeholder: isNew ? 'Kayıtta otomatik atanacak' : '' })}
                {/* #834 — SEÇİM alanı kaldırıldı: tür menüden gelir ve belgeye sabitlenir. */}
                {isReturnVariant && (
                  <div key="returnType" style={{ ...st.field, ...st.fieldWide }}>
                    <label style={st.label}>İade Türü</label>
                    <input style={{ ...s.inputView, borderRadius: 6 }} readOnly value={returnSubtypeLabel(form.type)} />
                  </div>
                )}
                {Field({ label: 'Matbu No', k: 'printedNo', mono: true, placeholder: 'Boş bırakılırsa baskıda otomatik atanır' })}
                <div key="dueDay" style={st.field}>
                  <label style={st.label}>Vade Günü</label>
                  <input type="number" style={{ ...(isView ? s.inputView : s.input), borderRadius: 6 }} value={form.dueDay ?? ''} readOnly={isView} placeholder="Gün" onChange={(e) => onDueDayChange(e.target.value)} />
                </div>
                <div key="documentDate" style={st.field}>
                  <label style={st.label}>İrsaliye Tarihi</label>
                  <input type="date" style={{ ...(isView ? s.inputView : s.input), borderRadius: 6 }} value={form.documentDate ?? ''} readOnly={isView} onChange={(e) => onDocDateChange(e.target.value)} />
                </div>
                <div key="dueDate" style={st.field}>
                  <label style={st.label}>Vade Tarihi</label>
                  <input type="date" style={{ ...(isView ? s.inputView : s.input), borderRadius: 6 }} value={form.dueDate ?? ''} readOnly={isView} onChange={(e) => onDueDateChange(e.target.value)} />
                </div>
                {Field({ label: 'Sevk Tarihi', k: 'shipDate', type: 'date' })}
                {SelectField({ label: 'Ödeme Tipi *', k: 'paymentTypeId', getLabel: getPaymentTypeLabel, fetchOptions: paymentTypeFetch })}
                <div style={{ ...st.field, ...st.fieldWide }}>
                  <label style={st.label}>
                    ETTN No
                    <EttnQrButton ettn={ettn} buttonStyle={{ ...s.btnSecondary, padding: '1px 8px', minWidth: 0, fontSize: 11, marginLeft: 6, height: 'auto' }} />
                  </label>
                  <input style={{ ...s.inputView, borderRadius: 6, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} readOnly value={ettn || (isNew ? 'Kayıttan sonra üretilecek' : '-')} />
                </div>
              </div>
            </div>
            <div style={st.bandCol}>
              <h3 style={st.sectionTitle}>👤 Müşteri ve Operasyon</h3>
              <div style={st.grid2}>
                <div style={{ ...st.field, ...st.fieldWide }}>
                  <label style={st.label}>Müşteri *</label>
                  {isView ? (
                    <input style={{ ...s.inputView, borderRadius: 6 }} readOnly value={customerName(form.customerId)} />
                  ) : (
                    <RelationPicker
                      config={RELATION_CONFIGS.customer}
                      value={form.customerId || ''}
                      selectedLabel={form.customerId ? customerName(form.customerId) : ''}
                      onChange={(val, item) => { setValue('customerId', val); if (val && item) setCustomerLabels((p) => ({ ...p, [val]: lookupLabel(item) })); if (val) applyCustomerDefaults(val); }}
                      placeholder="Müşteri ara…"
                    />
                  )}
                </div>
                {/* Talep 833 kural 2 — distribütör kullanıcısı kendi distribütörünü değiştiremez. */}
                {SelectField({ label: 'Distribütör', k: 'vendorId', getLabel: vendorName, fetchOptions: vendorFetch, locked: distributorUser.isDistributor })}
                {/* #759 Depo değişince onPick ile mevcut kalemlerin deposu da eşitlenir (changeWarehouse). */}
                {SelectField({ label: 'Depo *', k: 'warehouseId', getLabel: warehouseName, fetchOptions: warehouseFetch, onPick: changeWarehouse })}
                {Field({ label: 'ERP Id', k: 'erpId', mono: true, readOnly: true })}
                {Field({ label: 'Sipariş No', k: 'orderId', mono: true })}
              </div>
            </div>
            <div style={st.bandCol}>
              <h3 style={st.sectionTitle}>🧑‍💼 Satış Temsilcisi</h3>
              <div style={st.grid1}>
                {SelectField({ label: 'Temsilci *', k: 'representativeId', getLabel: representativeName, fetchOptions: repFetch })}
                <div style={st.field}>
                  <label style={st.label}>Bölge / Ekip</label>
                  <input style={{ ...s.inputView, borderRadius: 6 }} readOnly value={representativeRegion(form.representativeId) || '-'} />
                </div>
              </div>
            </div>
            </div>
          </section>

          {/* Daha Fazla (korunan/ikincil alanlar) */}
          <div style={st.moreBar} onClick={() => setMoreOpen((v) => !v)}>
            <span>{moreOpen ? '▾' : '▸'}</span> Daha Fazla (Makbuz, E-Arşiv, Özel Kod, Teslimat, Açıklamalar, Genel İskonto)
          </div>
          {moreOpen && (
            <section style={st.section}>
              <div style={st.band}>
                {Field({ label: 'Makbuz No', k: 'receiptNo' })}
                {Field({ label: 'E-Arşiv', k: 'eArchive' })}
                {Field({ label: 'Özel Kod', k: 'specialId' })}
                {Field({ label: 'Dispatch Id', k: 'dispatchId', mono: true, readOnly: true })}
                {Field({ label: 'Genel İskonto %1', k: 'discountPercentage1', type: 'number' })}
                {Field({ label: 'Genel İskonto %2', k: 'discountPercentage2', type: 'number' })}
                {Field({ label: 'Genel İskonto %3', k: 'discountPercentage3', type: 'number' })}
                {Field({ label: 'ÖTV Tutarı', k: 'specialConsumptionTaxAmount', type: 'number' })}
                {Field({ label: 'Yıl', k: 'year', type: 'number' })}
                {Field({ label: 'Teslimat Adresi', k: 'deliveryAdress', wide: true, multiline: true })}
                {Field({ label: 'Açıklama 1', k: 'description1', wide: true, multiline: true })}
                {Field({ label: 'Açıklama 2', k: 'description2', wide: true, multiline: true })}
              </div>
            </section>
          )}

          {/* ④ KALEM TABLOSU */}
          <div style={st.itemsCard}>
            <div style={st.itemsHead}>
              <h3 style={{ ...st.sectionTitle, margin: 0 }}>
                📦 İrsaliye Kalemleri
                <span style={{ color: muted, fontWeight: 600, marginLeft: 8 }}>
                  {filteredDetails.length}/{waybillDetails.length} kalem · {totalQty.toLocaleString('tr-TR')} adet
                </span>
              </h3>
              <div style={st.colSearch}>
                <input style={{ ...s.filterInput, height: 32, minWidth: 120 }} placeholder="Kod ara" value={colSearch.code} onChange={(e) => setColSearch((c) => ({ ...c, code: e.target.value }))} />
                <input style={{ ...s.filterInput, height: 32, minWidth: 140 }} placeholder="Ürün ara" value={colSearch.name} onChange={(e) => setColSearch((c) => ({ ...c, name: e.target.value }))} />
              </div>
            </div>
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={{ ...st.th, textAlign: 'center', width: 38 }}>#</th>
                    <th style={{ ...st.th, ...st.thLeft }}>Ürün Kod</th>
                    <th style={{ ...st.th, ...st.thLeft }}>Ürün Açıklaması</th>
                    <th style={st.th}>Miktar</th>
                    <th style={{ ...st.th, ...st.thLeft }}>Birim</th>
                    {/* #748 Stok, Miktar–Birim'den HEMEN SONRA kendi kolonunda (rozet kaldırıldı). */}
                    <th style={st.th} title="Belgede seçili depodaki kullanılabilir stok (satırın birimine çevrilmiş)">Stok</th>
                    {isConsignment && <th style={{ ...st.th, ...st.thLeft }}>Seri No</th>}
                    <th style={st.th}>Birim Fiyat</th>
                    <th style={st.th}>İsk-1%</th>
                    <th style={st.th}>İsk-2%</th>
                    <th style={st.th}>İsk-3%</th>
                    <th style={st.th}>İsk-4%</th>
                    <th style={st.th}>İskonto ₺</th>
                    <th style={st.th}>Net (KDV Hariç)</th>
                    <th style={st.th}>KDV%</th>
                    <th style={st.th}>KDV ₺</th>
                    <th style={st.th}>Brüt</th>
                    {!isView && <th style={{ ...st.th, width: 40 }} />}
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails.length === 0 && (
                    <tr><td colSpan={(isView ? 16 : 17) + (isConsignment ? 1 : 0)} style={{ ...st.td, textAlign: 'center', color: muted, padding: 24 }}>Kalem yok — aşağıdan ürün ekleyin.</td></tr>
                  )}
                  {filteredDetails.map((row, idx) => {
                    const label = String(productName(row.productId));
                    const code = label.split(' - ')[0];
                    const desc = label.split(' - ').slice(1).join(' - ') || label;
                    return (
                      <tr key={row.clientId}>
                        <td style={{ ...st.td, textAlign: 'center', color: muted }}>{idx + 1}</td>
                        <td style={{ ...st.td, ...st.tdLeft }}>{row.productId ? <span style={st.chip}>{code}</span> : '-'}</td>
                        <td style={{ ...st.td, ...st.tdLeft }}>{desc}</td>
                        <td style={st.td}>{isView ? <b>{row.quantity}</b> : <input inputMode="decimal" style={st.cellInput} value={row.quantity} onChange={(e) => updateDetailRow(row.clientId, 'quantity', sanitizeDecimal(e.target.value))} />}</td>
                        <td style={{ ...st.td, ...st.tdLeft }}>{renderUnitCell(row.productId, row.unit, (v) => changeRowUnit(row, v))}</td>
                        {/* Talep 748: eklenmiş kalemde de seçili depodaki stok görünür — kendi kolonunda. */}
                        <td style={st.td}>{renderStockCell(row.productId, row.unit)}</td>
                        {isConsignment && (
                          <td style={{ ...st.td, ...st.tdLeft }}>
                            {isView ? (row.serialNo || '-') : <input style={{ ...st.cellInput, textAlign: 'left', minWidth: 90 }} value={row.serialNo || ''} placeholder="Seri okut" onChange={(e) => updateDetailRow(row.clientId, 'serialNo', e.target.value)} />}
                          </td>
                        )}
                        <td style={st.td}>{isView ? formatMoney(row.unitPrice) : <input inputMode="decimal" style={st.cellInput} value={row.unitPrice} onChange={(e) => updateDetailRow(row.clientId, 'unitPrice', sanitizeDecimal(e.target.value))} title="Yetkiye bağlı düzenlenebilir" />}</td>
                        {[1, 2, 3, 4].map((n) => (
                          <td style={st.td} key={n}>{isView ? formatPercent(row[`discountPercentage${n}`]) : <input inputMode="decimal" style={st.iskInput} value={row[`discountPercentage${n}`] ?? ''} placeholder="0" onChange={(e) => updateDetailRow(row.clientId, `discountPercentage${n}`, sanitizeDecimal(e.target.value))} />}</td>
                        ))}
                        <td style={st.td}>{formatMoney(row.discountAmount)}</td>
                        <td style={{ ...st.td, ...st.netCell }}>{formatMoney(row.vatBase)}</td>
                        <td style={st.td}>{isView ? formatPercent(row.vatPercentage) : <input inputMode="decimal" style={st.iskInput} value={row.vatPercentage} onChange={(e) => updateDetailRow(row.clientId, 'vatPercentage', sanitizeDecimal(e.target.value))} />}</td>
                        <td style={st.td}>{formatMoney(row.vatAmount)}</td>
                        <td style={st.td}>{formatMoney(row.grossAmount)}</td>
                        {!isView && <td style={st.td}><button type="button" style={dangerBtn} onClick={() => removeDetailRow(row)}>×</button></td>}
                      </tr>
                    );
                  })}
                </tbody>
                {!isView && (
                  <tfoot>
                    <tr style={{ position: 'sticky', bottom: 0, background: 'var(--bg-subtle, #f9fafb)' }}>
                      <td style={{ ...st.td, textAlign: 'center', color: accent }}>＋</td>
                      <td style={{ ...st.td, ...st.tdLeft }} colSpan={2}>
                        <RelationPicker
                          config={productConfig}
                          value={draft.productId || ''}
                          selectedLabel={draft.productId ? productName(draft.productId) : ''}
                          onChange={(val, item) => setDraftProduct(val, item)}
                          placeholder={headerReady ? 'Ürün kodu yaz / ara…' : headerHint}
                          panelMinWidth={860}
                          selectedCode={draft.productId ? String(productName(draft.productId)).split(' - ')[0] : ''}
                          disabled={!headerReady}
                        />
                        {/* #766 Başlık dolmadan kalem eklenemez — gerekçe kullanıcıya YERİNDE söylenir. */}
                        {!headerReady && (
                          <span style={{ display: 'block', marginTop: 6, color: 'var(--text-secondary)', fontSize: 12 }}>{headerHint}.</span>
                        )}
                      </td>
                      <td style={st.td}><input ref={qtyRef} inputMode="decimal" style={st.cellInput} value={draft.quantity} onChange={(e) => setDraft((d) => recalcLine({ ...d, quantity: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); unitPriceRef.current?.focus(); } }} /></td>
                      <td style={{ ...st.td, ...st.tdLeft }}>{renderUnitCell(draft.productId, draft.unit, (v) => changeDraftUnit(v))}</td>
                      {/* Talep 748: ürün seçilir seçilmez, EKLEMEDEN ÖNCE depo stoğu görünür — kendi kolonunda. */}
                      <td style={st.td}>{renderStockCell(draft.productId, draft.unit)}</td>
                      {isConsignment && (
                        <td style={{ ...st.td, ...st.tdLeft }}>
                          <input style={{ ...st.cellInput, textAlign: 'left', minWidth: 90 }} value={draft.serialNo || ''} placeholder="Seri okut" onChange={(e) => setDraft((d) => ({ ...d, serialNo: e.target.value }))} />
                        </td>
                      )}
                      <td style={st.td}><input ref={unitPriceRef} inputMode="decimal" style={st.cellInput} value={draft.unitPrice} onChange={(e) => setDraft((d) => recalcLine({ ...d, unitPrice: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); isk1Ref.current?.focus(); } }} /></td>
                      <td style={st.td}><input ref={isk1Ref} inputMode="decimal" style={st.iskInput} value={draft.discountPercentage1} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage1: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.discountPercentage2} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage2: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.discountPercentage3} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage3: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.discountPercentage4} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage4: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={{ ...st.td, color: muted, fontSize: 11 }} colSpan={2}>Enter ile satırı ekle</td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.vatPercentage} onChange={(e) => setDraft((d) => recalcLine({ ...d, vatPercentage: sanitizeDecimal(e.target.value) }))} /></td>
                      <td style={st.td} />
                      <td style={st.td} colSpan={2}><button type="button" style={{ ...s.btnPrimary, padding: '6px 12px' }} onClick={commitDraft}>+ Ekle</button></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* ⑤ STICKY TOPLAM ÇUBUĞU (İrsaliye'de tahsilat yok) */}
        <div style={st.totbar}>
          <div style={st.totCell}><span style={st.totLabel}>Brüt Tutar</span><span style={st.totValue}>{formatMoney(totals.grossAmount)}</span></div>
          <div style={st.totCell}><span style={st.totLabel}>İskonto</span><span style={{ ...st.totValue, color: 'var(--danger, #e2445c)' }}>−{formatMoney(totals.discountAmount)}</span></div>
          <div style={st.totCell}><span style={st.totLabel}>Net (KDV Hariç)</span><span style={st.totValue}>{formatMoney(totals.vatBase)}</span></div>
          <div style={st.totCell}><span style={st.totLabel}>KDV Toplamı</span><span style={st.totValue}>{formatMoney(totals.vatAmount)}</span></div>
          {totals.sct > 0 && <div style={st.totCell}><span style={st.totLabel}>ÖTV</span><span style={st.totValue}>{formatMoney(totals.sct)}</span></div>}
          <div style={st.grandCell}><span style={{ ...st.totLabel, color: 'rgba(255,255,255,0.8)' }}>GENEL TOPLAM</span><span style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(totals.netAmount)}</span></div>
        </div>
      </div>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: 'var(--danger,#e2445c)' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, lineHeight: 1.6 }}>Bu irsaliyeyi silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}><button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div>
          </div>
        </div>
      )}

      <DocumentPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        docType={1}
        documentTip={documentTip}
        year={toInt(form.year)}
        documentId={id}
        title={form.textId}
      />

      <Toast toast={toast} />
    </div>
  );
}

export default WaybillDetailPage;
